import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { cartRequestHeaders } from './cartSession';

// Variables de entorno - soporta ambos formatos
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://twidpdwinrndvvlujgij.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || 'sb_publishable_K6lFYx92BNdefOpa90RfXA_ofWu6PkC';

// Data source: 'supabase' | 'mock' (default: 'mock')
export const DATA_SOURCE = import.meta.env.VITE_DATA_SOURCE || 'supabase';

// Verificar si Supabase está configurado
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

// Diagnóstico en producción
if (typeof window !== 'undefined') {
  console.log('[Supabase] URL:', supabaseUrl ? 'OK' : 'MISSING');
  console.log('[Supabase] Key:', supabaseAnonKey ? 'OK' : 'MISSING');
  console.log('[Supabase] DATA_SOURCE:', DATA_SOURCE);
  console.log('[Supabase] isConfigured:', isSupabaseConfigured);
}

// ── Singleton ─────────────────────────────────────────────────────────────────
// IMPORTANT: Only ONE instance must exist in the entire app so all parts of the
// app (including auth, RLS functions like admin_role(), etc.) share the same
// session. A second createClient() call produces a different instance that
// doesn't inherit the authenticated session, causing admin_role() to get
// auth.uid() = null and RLS checks to silently fail.
let supabaseInstance: SupabaseClient | null = null;

export const getSupabaseClient = (): SupabaseClient | null => {
  if (!isSupabaseConfigured) return null;
  if (!supabaseInstance) {
    supabaseInstance = createClient(supabaseUrl!, supabaseAnonKey!, {
      global: {
        fetch: (input, init) => {
          const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
          const headers = url.origin === new URL(supabaseUrl).origin && url.pathname.startsWith('/rest/v1/')
            ? cartRequestHeaders(init?.headers ?? (input instanceof Request ? input.headers : undefined))
            : init?.headers;
          return fetch(input, { ...init, headers });
        },
      },
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      },
    });
  }
  return supabaseInstance;
};

// Single shared client — ALL imports must use this same reference.
// Do NOT call createClient() anywhere else in the codebase.
export const supabaseClient = getSupabaseClient();

// Helper para determinar si usar Supabase o mock
export const shouldUseSupabase = (): boolean => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlDataSource = urlParams.get('data');
    if (urlDataSource === 'mock') return false;
    if (urlDataSource === 'supabase' && isSupabaseConfigured) return true;
    try {
      const localDataSource = localStorage.getItem('data_source');
      if (localDataSource === 'mock') return false;
      if (localDataSource === 'supabase' && isSupabaseConfigured) return true;
    } catch {
      // ignore
    }
  }
  if (DATA_SOURCE === 'supabase' && isSupabaseConfigured) return true;
  if (isSupabaseConfigured) return true;
  return false;
};

// Helper para queries con fallback automático a mock
export async function queryWithFallback<T>(
  supabaseQuery: () => PromiseLike<{ data: T | null; error: unknown }>,
  mockData: T,
  _tableName: string
): Promise<{ data: T; source: 'supabase' | 'mock' }> {
  if (!shouldUseSupabase()) return { data: mockData, source: 'mock' };
  try {
    const { data, error } = await supabaseQuery();
    if (error) return { data: mockData, source: 'mock' };
    if (!data || (Array.isArray(data) && data.length === 0)) return { data: mockData, source: 'mock' };
    return { data: data as T, source: 'supabase' };
  } catch {
    return { data: mockData, source: 'mock' };
  }
}

export default supabaseClient;

